/**
 * MobileDrawer - Slide-out drawer for mobile navigation
 *
 * BYK-168: Responsive collapsing for smaller screens
 * Provides touch-friendly slide-out panels for mobile devices.
 */

import { useEffect, useRef, useCallback } from "react";
import { motion, AnimatePresence, useDragControls } from "framer-motion";
import { X } from "lucide-react";
import { cn } from "@/lib/utils";

// ============================================================================
// Types
// ============================================================================

export interface MobileDrawerProps {
  /** Whether the drawer is open */
  open: boolean;
  /** Callback when drawer should close */
  onClose: () => void;
  /** Which side the drawer slides from */
  side: "left" | "right";
  /** Drawer title */
  title?: string;
  /** Drawer content */
  children: React.ReactNode;
  /** Custom class name */
  className?: string;
}

// ============================================================================
// Constants
// ============================================================================

const SWIPE_THRESHOLD = 50; // pixels to trigger close

// ============================================================================
// Component
// ============================================================================

export function MobileDrawer({
  open,
  onClose,
  side,
  title,
  children,
  className,
}: MobileDrawerProps) {
  const dragControls = useDragControls();
  const drawerRef = useRef<HTMLDivElement>(null);

  // Use ref pattern to avoid stale closure issues and prevent unnecessary re-renders
  const onCloseRef = useRef(onClose);
  useEffect(() => {
    onCloseRef.current = onClose;
  });

  // Close on escape
  useEffect(() => {
    if (!open) return;

    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        onCloseRef.current();
      }
    };

    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [open]); // onClose removed - using ref pattern instead

  // Prevent body scroll when drawer is open
  useEffect(() => {
    if (open) {
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "";
    }
    return () => {
      document.body.style.overflow = "";
    };
  }, [open]);

  // Handle drag end
  const handleDragEnd = useCallback(
    (_: unknown, info: { offset: { x: number } }) => {
      const threshold = side === "left" ? -SWIPE_THRESHOLD : SWIPE_THRESHOLD;
      if (
        (side === "left" && info.offset.x < threshold) ||
        (side === "right" && info.offset.x > threshold)
      ) {
        onCloseRef.current();
      }
    },
    [side] // onClose removed - using ref pattern instead
  );

  // Animation variants
  const drawerVariants = {
    closed: {
      x: side === "left" ? "-100%" : "100%",
      transition: { type: "spring", damping: 25, stiffness: 300 },
    },
    open: {
      x: 0,
      transition: { type: "spring", damping: 25, stiffness: 300 },
    },
  };

  const backdropVariants = {
    closed: { opacity: 0 },
    open: { opacity: 1 },
  };

  return (
    <AnimatePresence>
      {open && (
        <>
          {/* Backdrop */}
          <motion.div
            className="fixed inset-0 z-50 bg-black/50 backdrop-blur-sm"
            variants={backdropVariants}
            initial="closed"
            animate="open"
            exit="closed"
            onClick={onClose}
            aria-hidden="true"
          />

          {/* Drawer */}
          <motion.div
            ref={drawerRef}
            className={cn(
              "fixed top-0 z-50 h-full w-[85vw] max-w-sm bg-white shadow-2xl",
              side === "left" ? "left-0" : "right-0",
              className
            )}
            variants={drawerVariants}
            initial="closed"
            animate="open"
            exit="closed"
            drag="x"
            dragControls={dragControls}
            dragConstraints={{ left: 0, right: 0 }}
            dragElastic={0.1}
            onDragEnd={handleDragEnd}
            role="dialog"
            aria-modal="true"
            aria-label={title || "Navigation drawer"}
          >
            {/* Header */}
            <div className="flex items-center justify-between border-b border-gray-200 px-4 py-3">
              <h2 className="text-lg font-semibold text-gray-900">{title}</h2>
              <button
                onClick={onClose}
                className={cn(
                  "rounded-lg p-2 text-gray-400",
                  "hover:bg-gray-100 hover:text-gray-600",
                  "focus:outline-none focus:ring-2 focus:ring-blue-500"
                )}
                aria-label="Close drawer"
              >
                <X className="h-5 w-5" />
              </button>
            </div>

            {/* Swipe indicator */}
            <div
              className={cn(
                "absolute top-1/2 -translate-y-1/2",
                "flex h-12 w-1 items-center justify-center",
                side === "left" ? "right-1" : "left-1"
              )}
            >
              <div className="h-8 w-1 rounded-full bg-gray-300" />
            </div>

            {/* Content */}
            <div className="h-[calc(100%-60px)] overflow-y-auto">
              {children}
            </div>
          </motion.div>
        </>
      )}
    </AnimatePresence>
  );
}

export default MobileDrawer;
